import React, { createContext, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';

import { IUser } from '~/store/modules/users/types';

import api, { signOut } from '../services/api';

import './authcss.css';

interface AuthState {
  token: string;
  user: IUser;
  domain: string;
}

interface SignInCredentials {
  username: string;
  password: string;
}

interface AuthContextData {
  loading: boolean;
  forgotPasswordSuccess: boolean | null;
  resetForgotPasswordSuccess(): void;
  changeForgotPassword(newPassword: string, token: string): void;
  user: IUser;
  isAuthenticated: () => boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  forgotPassword(username: string): Promise<void>;
  checkPermission(permissions: Array<string>): boolean;
  checkProfile(profileName: string): boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<
    boolean | null
  >(null);
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Afferolab:token');
    const userStorage = localStorage.getItem('@Afferolab:user');
    const domain = localStorage.getItem('@Afferolab:domain');

    if (token && userStorage && domain) {
      const user = JSON.parse(userStorage);
      return {
        token,
        user,
        domain,
      };
    }

    return {} as AuthState;
  });

  const isAuthenticated = useCallback(
    () => !!localStorage.getItem('@Afferolab:user'),
    [],
  );

  const signIn = useCallback(async ({ username, password }) => {
    setLoading(true);

    try {
      const userAuth = await api.post('/auth', { username, password });
      const { token, user, domain } = userAuth.data;

      localStorage.setItem('@Afferolab:token', token);
      localStorage.setItem('@Afferolab:user', JSON.stringify(user));
      localStorage.setItem('@Afferolab:domain', domain);
      localStorage.setItem('@Afferolab:orgKey', user.orgKey);

      setData({ token, user, domain });
      setLoading(false);

      if ((user && user.orgId === 180) || (user && user.orgId === 304)) {
        setTimeout(() => {
          toast.info(
            <>
              <p>Olá, Fornecedores!</p>
              <p>
                Devido ao nosso recesso de final de ano, faremos o fechamento no
                dia 06/12.
              </p>
              <p>
                Desta forma, os serviços prestados entre o período 21/11/2025 à
                05/12/2025, deverão subir em nosso Portal Wedo até o dia
                06/12/2025.A realização do pagamento será em 29/12/2025.
              </p>
              <p>
                Para os serviços prestados no período de 06/12/2025 a
                20/01/2026, as NFs deverão ser emitidas a partir de 02/01/2026.
                Não haverá alteração na janela, as NFs deverão ser inseridas no
                Portal até 20/01/2026 e o pagamento será realizado em
                31/01/2026.
              </p>
              <p> Para dúvidas, a equipe administrativa, está à disposição!</p>
            </>,
            {
              position: 'top-center',
              autoClose: false,
              type: 'default',
              className: 'teste',
            },
          );
        }, 3000);
      }
    } catch (e) {
      setLoading(false);
      toast.error('E-mail ou senha incorretos, tente novamente.');
    }
  }, []);

  const checkPermission = (permissions: Array<string>): boolean => {
    if (!data.user.permissions || data.user.permissions.length === 0) {
      return false;
    }

    if (permissions.length < 1) {
      return true;
    }

    return permissions.some((permission) =>
      data.user.permissions.includes(permission),
    );
  };

  const checkProfile = (profileName: string): boolean => {
    if (!data.user.profiles || data.user.profiles.length === 0) return false;

    if (
      data.user.profiles
        .map((profile) => profile.name === profileName)
        .includes(true)
    )
      return true;

    return false;
  };

  const resetForgotPasswordSuccess = () => {
    setForgotPasswordSuccess(null);
  };

  const forgotPassword = useCallback(async (username: string) => {
    setForgotPasswordSuccess(null);
    setLoading(true);
    try {
      await api.post('/users/forgot-password', { username });
      setForgotPasswordSuccess(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(
        'A solicitação não pode ser concluída. Verifique se os dados estão corretos.',
      );
    }
  }, []);

  const changeForgotPassword = useCallback(
    async (newPassword: string, token: string) => {
      setForgotPasswordSuccess(null);
      setLoading(true);
      try {
        await api.post('/users/change-forgot-password', { newPassword, token });
        setForgotPasswordSuccess(true);
        setLoading(false);
      } catch (error) {
        setForgotPasswordSuccess(false);
        setLoading(false);
        toast.error(
          'A solicitação não pode ser concluída. Verifique se os dados estão corretos.',
        );
      }
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        isAuthenticated,
        loading,
        forgotPasswordSuccess,
        resetForgotPasswordSuccess,
        changeForgotPassword,
        signIn,
        signOut,
        forgotPassword,
        checkPermission,
        checkProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be user within an AuthProvider.');
  }

  return context;
}

export { AuthProvider, useAuth };
